import React, { useState, useEffect } from 'react';
import { timelineAPI } from '../utils/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CheckCircle, FileText, Clock, Send, Upload, AlertTriangle,
  UserCheck, FileCheck, DollarSign, Globe, MessageSquare, StickyNote
} from 'lucide-react';

const EVENT_ICONS = {
  case_created: Clock,
  stage_changed: CheckCircle,
  stage_progressed: CheckCircle,
  document_uploaded: Upload,
  document_reviewed: FileCheck,
  agreement_signed: UserCheck,
  agreement_sent_for_signing: Send,
  agreement_signed_zoho: UserCheck,
  final_application_uploaded: FileText,
  final_application_approved: CheckCircle,
  final_application_changes_requested: AlertTriangle,
  visa_fee_paid: DollarSign,
  application_filed: Globe,
  visa_decision_received: CheckCircle,
  complaint_filed: MessageSquare,
  sample_document_uploaded: FileText,
  service_extended: Clock,
  note_added: StickyNote,
};

const EVENT_COLORS = {
  case_created: 'bg-blue-100 text-blue-600 border-blue-200',
  stage_changed: 'bg-teal-100 text-teal-600 border-teal-200',
  stage_progressed: 'bg-green-100 text-green-600 border-green-200',
  document_uploaded: 'bg-purple-100 text-purple-600 border-purple-200',
  document_reviewed: 'bg-indigo-100 text-indigo-600 border-indigo-200',
  agreement_signed: 'bg-green-100 text-green-600 border-green-200',
  agreement_sent_for_signing: 'bg-orange-100 text-orange-600 border-orange-200',
  agreement_signed_zoho: 'bg-green-100 text-green-600 border-green-200',
  final_application_uploaded: 'bg-blue-100 text-blue-600 border-blue-200',
  final_application_approved: 'bg-green-100 text-green-600 border-green-200',
  final_application_changes_requested: 'bg-yellow-100 text-yellow-600 border-yellow-200',
  visa_fee_paid: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  application_filed: 'bg-teal-100 text-teal-600 border-teal-200',
  visa_decision_received: 'bg-green-100 text-green-600 border-green-200',
  complaint_filed: 'bg-red-100 text-red-600 border-red-200',
  sample_document_uploaded: 'bg-gray-100 text-gray-600 border-gray-200',
  service_extended: 'bg-blue-100 text-blue-600 border-blue-200',
  note_added: 'bg-gray-100 text-gray-600 border-gray-200',
};

export default function CaseTimeline({ caseId }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (caseId) {
      fetchTimeline();
    }
  }, [caseId]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      const response = await timelineAPI.getTimeline(caseId);
      setTimeline(response.data || []);
    } catch (err) {
      console.error('Error fetching timeline:', err);
      setError('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-8 w-8 border-4 border-teal-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-red-500">
        {error}
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        No timeline events yet
      </div>
    );
  }

  // Group timeline by date
  const groupedTimeline = timeline.reduce((groups, event) => {
    const date = formatDate(event.timestamp);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(event);
    return groups;
  }, {});

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="h-5 w-5" />
          Case Timeline
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />

          {Object.entries(groupedTimeline).map(([date, events], groupIndex) => (
            <div key={date} className="mb-6">
              {/* Date Header */}
              <div className="relative flex items-center mb-4">
                <div className="absolute left-2 w-4 h-4 bg-gray-300 rounded-full border-2 border-white z-10" />
                <div className="ml-10 text-sm font-semibold text-gray-500">{date}</div>
              </div>

              {/* Events for this date */}
              {events.map((event, eventIndex) => {
                const IconComponent = EVENT_ICONS[event.event_type] || FileText;
                const colorClass = EVENT_COLORS[event.event_type] || 'bg-gray-100 text-gray-600 border-gray-200';

                return (
                  <div key={event.id} className="relative flex items-start mb-4 ml-2">
                    {/* Event Icon */}
                    <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center border-2 ${colorClass} z-10`}>
                      <IconComponent className="h-4 w-4" />
                    </div>

                    {/* Event Content */}
                    <div className="ml-4 flex-1 bg-white rounded-lg border p-3 shadow-sm">
                      <div className="flex justify-between items-start mb-1">
                        <h4 className="font-medium text-gray-900">{event.title}</h4>
                        <span className="text-xs text-gray-500">{formatTime(event.timestamp)}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{event.description}</p>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs">
                          {event.actor_role}
                        </Badge>
                        <span className="text-xs text-gray-500">by {event.actor_name}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Compact version for embedding in other pages
export function CaseTimelineCompact({ caseId, limit = 5 }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (caseId) {
      fetchTimeline();
    }
  }, [caseId]);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      const response = await timelineAPI.getTimeline(caseId);
      setTimeline((response.data || []).slice(0, limit));
    } catch (err) {
      console.error('Error fetching timeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="animate-pulse h-20 bg-gray-100 rounded" />;
  }

  return (
    <div className="space-y-2">
      {timeline.map((event) => {
        const IconComponent = EVENT_ICONS[event.event_type] || FileText;
        const colorClass = EVENT_COLORS[event.event_type] || 'bg-gray-100 text-gray-600';

        return (
          <div key={event.id} className="flex items-start gap-3 p-2 rounded hover:bg-gray-50">
            <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${colorClass}`}>
              <IconComponent className="h-3 w-3" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{event.title}</p>
              <p className="text-xs text-gray-500">{formatRelativeTime(event.timestamp)}</p>
            </div>
          </div>
        );
      })}
      {timeline.length === 0 && (
        <p className="text-sm text-gray-500 text-center py-2">No recent activity</p>
      )}
    </div>
  );
}
